/** @typedef {{ neutral: string, vimeo: string, youtube: string }} VideoJsAssets */
/** @typedef {{ vimeo?: string[], youtube?: string[] }} VideoPreconnects */

let videoJsLoadPromise = null

const hasPreconnect = (origin) =>
  Boolean(document.querySelector(`link[rel="preconnect"][href="${origin}"]`))

/**
 * @param {string | undefined} provider
 * @param {VideoPreconnects | undefined} originsMap
 */
export const injectVideoPreconnects = (provider, originsMap) => {
  const origins = originsMap?.[provider]
  if (!origins?.length) return

  origins.forEach((origin) => {
    if (hasPreconnect(origin)) return

    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = origin
    document.head.appendChild(link)
  })
}

/**
 * Dynamically import Video.js CDN modules (webpackIgnore keeps Mix from bundling them).
 * @param {VideoJsAssets | undefined} videoJsAssets
 * @param {string | undefined} provider
 */
export const ensureVideoJsLoaded = (videoJsAssets, provider) => {
  if (customElements.get('video-player') && customElements.get('video-neutral-skin')) {
    const mediaTag = provider === 'youtube' ? 'youtube-video' : 'vimeo-video'
    if (!provider || customElements.get(mediaTag)) {
      return Promise.resolve()
    }
  }

  if (!videoJsAssets?.neutral) return Promise.resolve()
  if (videoJsLoadPromise) return videoJsLoadPromise

  videoJsLoadPromise = (async () => {
    await import(/* webpackIgnore: true */ videoJsAssets.neutral)

    if (provider === 'youtube' && videoJsAssets.youtube) {
      await import(/* webpackIgnore: true */ videoJsAssets.youtube)
    } else if (videoJsAssets.vimeo) {
      await import(/* webpackIgnore: true */ videoJsAssets.vimeo)
    }

    // Prefer loading both adapters when available so Highway navigations
    // between Vimeo and YouTube projects do not need a second fetch.
    if (provider !== 'youtube' && videoJsAssets.youtube) {
      await import(/* webpackIgnore: true */ videoJsAssets.youtube).catch(() => {})
    }
    if (provider === 'youtube' && videoJsAssets.vimeo) {
      await import(/* webpackIgnore: true */ videoJsAssets.vimeo).catch(() => {})
    }

    await customElements.whenDefined('video-player')
    await customElements.whenDefined('video-neutral-skin')
  })().catch((error) => {
    videoJsLoadPromise = null
    throw error
  })

  return videoJsLoadPromise
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {{ videojs?: VideoJsAssets, videoPreconnects?: VideoPreconnects } | undefined} assets
 */
export const prepareProjectPage = async (view, assets) => {
  const player = view?.querySelector?.('[data-module-player]')
  if (!player || !assets) return

  injectVideoPreconnects(player.dataset.provider, assets.videoPreconnects)
  await ensureVideoJsLoaded(assets.videojs, player.dataset.provider)
}
